import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getAlbumById, getImagesByAlbum } from "../../api/requests";
import { Button, Card, Container, Modal } from "react-bootstrap";
import Navbar from "../Navigation/Navbar";

const AlbumDetail = () => {
    const { id } = useParams();
    const [album, setAlbum] = useState(null);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [viewerIndex, setViewerIndex] = useState(0);

    useEffect(() => {
        const fetchAlbumAndImages = async () => {
            try {
                setLoading(true);
                const [albumData, imgs] = await Promise.all([
                    getAlbumById(id),
                    getImagesByAlbum(id)
                ]);
                setAlbum(albumData || null);
                setImages(imgs || []);
            } catch (err) {
                console.error(err);
                setAlbum(null);
                setImages([]);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchAlbumAndImages();
    }, [id]);

    const openViewer = (imgs, idx) => {
        setViewerImages(imgs);
        setViewerIndex(idx);
        setViewerOpen(true);
    };
    const closeViewer = () => setViewerOpen(false);

    const showPrev = () => setViewerIndex(i => Math.max(0, i - 1));
    const showNext = () => setViewerIndex(i => Math.min(viewerImages.length - 1, i + 1));

    // Masonry CSS (wie in der Gallery-Übersicht)
    const masonryStyle = {
        columnCount: 1,
        columnGap: "16px",
        maxWidth: 1200,
        margin: "0 auto"
    };
    if (window.innerWidth >= 1024) masonryStyle.columnCount = 3;
    else if (window.innerWidth >= 640) masonryStyle.columnCount = 2;

    return (
        <>
            <Navbar />
            <Container>
                <Card>
                    <Card.Header>
                        <Card.Title>{album?.title || "Album"}</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <div style={{ marginBottom: 12 }}>
                            <Button as={Link} to="/galerie" variant="secondary">
                                Zurück zur Galerie
                            </Button>
                        </div>
                        {album?.description && <p className="plain-text">{album.description}</p>}
                        {loading ? (
                            <p>Lade Album...</p>
                        ) : images.length === 0 ? (
                            <p>Keine öffentlichen Bilder in diesem Album.</p>
                        ) : (
                            <div style={masonryStyle}>
                                {images.map((image, idx) => (
                                    <figure
                                        key={image.id}
                                        tabIndex={0}
                                        style={{
                                            breakInside: "avoid",
                                            margin: 0,
                                            marginBottom: 16,
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                            borderRadius: 12,
                                            overflow: "hidden",
                                            background: "#fff",
                                            position: "relative"
                                        }}
                                    >
                                        <a
                                            href={image.image}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            tabIndex={-1}
                                            onClick={e => {
                                                e.preventDefault();
                                                openViewer(images, idx);
                                            }}
                                            style={{ display: "block" }}
                                        >
                                            <img
                                                src={image.image}
                                                alt={image.title || `Foto aus Album ${album?.title || ""}`}
                                                loading="lazy"
                                                decoding="async"
                                                width="100%"
                                                style={{
                                                    width: "100%",
                                                    aspectRatio: "4/3",
                                                    objectFit: "cover",
                                                    display: "block"
                                                }}
                                            />
                                        </a>
                                        <figcaption
                                            style={{
                                                position: "absolute",
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                padding: "16px 12px 8px 12px",
                                                background: "linear-gradient(0deg,rgba(0,0,0,0.7) 70%,rgba(0,0,0,0.0) 100%)",
                                                color: "#fff",
                                                fontSize: 15,
                                                minHeight: 48,
                                                opacity: 0.95,
                                                transition: "opacity 0.2s",
                                                pointerEvents: "none"
                                            }}
                                            className="gallery-caption"
                                        >
                                            <div style={{ fontWeight: 600, fontSize: 17 }}>
                                                {image.title || "Ohne Titel"}
                                            </div>
                                            {image.photographer && (
                                                <div style={{ fontSize: 13, opacity: 0.85 }}>
                                                    © {image.photographer}
                                                </div>
                                            )}
                                            {image.description && (
                                                <div style={{ fontSize: 13, marginTop: 2, opacity: 0.85 }}>
                                                    {image.description.substring(0, 120)}
                                                </div>
                                            )}
                                        </figcaption>
                                        <style>{`
                                            figure:focus-visible .gallery-caption,
                                            figure:hover .gallery-caption {
                                                opacity: 1 !important;
                                                pointer-events: auto;
                                            }
                                            @media (hover: none) {
                                                .gallery-caption {
                                                    opacity: 1 !important;
                                                }
                                            }
                                        `}</style>
                                    </figure>
                                ))}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Container>
            {/* Modal-Viewer */}
            <Modal show={viewerOpen} onHide={closeViewer} centered size="lg">
                <Modal.Body className="text-center">
                    {viewerImages.length > 0 && (
                        <>
                            <img
                                src={viewerImages[viewerIndex].image}
                                alt={viewerImages[viewerIndex].title}
                                className="img-fluid"
                                style={{ maxHeight: "80vh" }}
                            />
                            <p className="mt-2">{viewerImages[viewerIndex].title}</p>
                            <p className="plain-text">{viewerImages[viewerIndex].description}</p>
                            <p><small>© {viewerImages[viewerIndex].photographer}</small></p>
                            <div>
                                <Button variant="secondary" onClick={showPrev} disabled={viewerIndex === 0}>◀ Zurück</Button>
                                <Button variant="secondary" onClick={showNext} disabled={viewerIndex === viewerImages.length - 1} className="ms-2">Weiter ▶</Button>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default AlbumDetail;
